<template>
  <full-width>
    <template v-if="hasInit">
      <back-button class="print:hidden" />
      <div class="block mx-auto print:hidden sm:max-w-lg sm:w-full sm:p-6">
        <!-- Heading: Receipt # and tooltip -->
        <div class="flex justify-between px-4 py-2 text-white tmsk-blue-bg">
          <h1 class="font-bold">
            {{ transaction.status === "exchanged" ? "Exchange" : "Receipt" }}:
            {{ transaction.uuid.split("-")[0] }}
          </h1>
          <div class="flex items-center justify-end float-right space-x-2">
            <button
              v-if="!isPayLahTile"
              data-e2e-attr="print-receipt-button"
              class="p-1 bg-white rounded-full tmsk-blue-text lg:block"
              @click="printPage"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                ></path>
              </svg>
            </button>
            <tooltip color="white" height="h-auto">
              <p>
                No refunds or exchanges after collection for hygiene reasons.
                Please visit the
                <a
                  target="_blank"
                  href="https://stayprepared.sg/masks-faq"
                  class="font-bold text-red-600 underline"
                  >FAQ</a
                >
                section for more information.
              </p>
            </tooltip>
          </div>
        </div>
        <!-- Order Instance -->
        <div class="border-2 tmsk-blue-border">
          <div v-for="(item, index) in transaction.items" :key="index">
            <!-- Items -->
            <div class="px-4 py-12 border-t-2 tmsk-blue-text tmsk-blue-border">
              <div class="mb-4 md:flex">
                <!-- Details -->
                <div class="items-center justify-center block md:justify-start">
                  <img
                    :src="getAssets(item.sku)"
                    :alt="item.name"
                    class="w-1/2 mx-auto md:w-4/5 md:ml-0 ie11-small-img"
                  />
                  <h2
                    class="w-full mx-auto my-4 text-xl font-bold text-center md:w-4/5 tmsk-blue-text md:mt-4 md:ml-0"
                  >
                    {{ item.name }}
                  </h2>
                </div>
                <!-- QR code -->
                <!-- <button
                  class="text-sm text-white rounded tmsk-blue-bg h-15 sm:p-2 md:text-base"
                >Redemption QR</button> -->
                <div class="relative w-full h-full">
                  <template
                    v-if="
                      (item.qtyUncollected === 0 &&
                        ['success', 'exchanged'].includes(
                          transaction.status
                        )) ||
                      ['refunded'].includes(transaction.status)
                    "
                  >
                    <div
                      class="absolute z-20 w-full h-full bg-white"
                      style="opacity: 0.9"
                    />
                    <p
                      class="absolute z-30 flex items-center justify-center w-full h-full text-2xl font-extrabold text-red-600"
                    >
                      <template
                        v-if="
                          item.qtyUncollected === 0 &&
                          ['success', 'exchanged'].includes(transaction.status)
                        "
                      >
                        COLLECTED
                      </template>
                      <template
                        v-else-if="['refunded'].includes(transaction.status)"
                      >
                        REFUNDED
                      </template>
                    </p>
                  </template>
                  <div
                    class="flex items-center justify-center w-56 h-56 mx-auto sm:w-64 sm:h-64"
                  >
                    <canvas
                      class="absolute w-56 h-56 mx-auto sm:w-64 sm:h-64 canvas"
                    />
                    <qr-code
                      class="z-10 w-48 h-48 sm:w-56 sm:h-56"
                      level="Q"
                      :value="
                        (item.qtyUncollected === 0 &&
                          ['success', 'exchanged'].includes(
                            transaction.status
                          )) ||
                        transaction.status === 'refunded'
                          ? ''
                          : item.qr
                      "
                      renderAs="canvas"
                      :size="activeBreakpoint === 'sm' ? 192 : 224"
                    />
                  </div>
                </div>
              </div>
              <p data-e2e-attr="print-receipt-price-display">
                <span class="font-bold">Price: </span>{{
                  transaction.status === "exchanged"
                    ? "Not For Sale"
                    : `$${item.price.toFixed(2)}/mask (RRP: $${item.rrp.toFixed(2)}/mask)`}}
              </p>
              <p>
                <span class="font-bold">Collected:</span>
                {{ item.qtyTotal - item.qtyUncollected }} /
                {{ item.qtyTotal }}
              </p>
              <p>
                <span class="font-bold">Amount:</span>
                {{
                  transaction.status === "exchanged"
                    ? "Not For Sale"
                    : `$${(item.qtyTotal * item.price).toFixed(2)}`
                }}
              </p>
              <p
                v-if="
                  (transaction.status === 'success' && !isCollected) ||
                  transaction.status === 'exchanged'
                "
                class="mt-6 text-red-700"
              >
                No partial refund is allowed on this order.
              </p>
              <p
                v-if="limitedLocationsSkuId === item.sku"
                class="mt-2 text-red-700"
                data-e2e-attr="order-receipt-qrcode-collection"
              >Scan QR code to collect your mask at <span
                  data-e2e-attr="order-receipt-sku-available-location-text"
                  class="font-bold underline cursor-pointer"
                  @click="routeToLocateVM(item.sku)"
                >Community Centres/Clubs, Plaza Singapura or Temasek Shophouse only.
                </span>
              </p>
              <p v-else class="mt-2 text-red-700"
                 data-e2e-attr="order-receipt-qrcode-collection">
                Scan QR code to collect your mask from <span
                  data-e2e-attr="order-receipt-sku-available-location-text"
                  class="font-bold underline cursor-pointer"
                  @click="routeToLocateVM(item.sku)"
                >over 800 locations island-wide.
                </span>
              </p>
            </div>
          </div>
        </div>
        <div class="flex justify-center px-4 py-2 text-white tmsk-blue-bg">
          <h1 class="font-bold">Total: ${{ totalAmount.toFixed(2) }}</h1>
        </div>
      </div>

      <printable-receipt :transaction="transaction" />
    </template>
  </full-width>
</template>

<script>
import beepAPI from '../../mixins/beepAPI'
import BackButton from '../../components/BackButton'
import FullWidth from '../../components/Layout/FullWidth'
import Tooltip from '../../components/Tooltip'
import PrintableReceipt from '../../components/printableReceipt/PrintableReceipt'

import QrCode from 'qrcode.vue'
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'OrderReceipt',
  metaInfo () {
    return {
      title: 'My Orders',
      // meta: [
      //   { property: 'og:title', content: 'My Orders' },
      //   { property: 'og:url', content: window.location.href },
      //   { property: 'og:site_name', content: 'My Orders' },
      //   { property: 'og:image', content: 'https://s3-ap-southeast-1.amazonaws.com/mask.stayprepared.sg/assets/staymasked-og-01.jpg' }
      // ],
      link: [{ rel: 'canonical', href: window.location.href }]
    }
  },
  components: {
    BackButton,
    FullWidth,
    QrCode,
    Tooltip,
    PrintableReceipt
  },
  mixins: [beepAPI],
  props: {},
  data () {
    return {
      hasInit: false,
      receiptId: this.$route.params.receipt,
      skuSortOrder: [
        'SM6_MASK_M',
        'SM6_MASK_L',
      ],
      limitedLocationsSkuId: ''
    }
  },
  computed: {
    ...mapState(['isLoggedIn', 'transactionHistory', 'mobileNumbers', 'isPayLahTile']),
    ...mapGetters(['activeBreakpoint']),

    listofTransactions () {
      const transactions = this.transactionHistory
        .filter((txn) =>
          ['success', 'refunded', 'exchanged'].includes(txn.status)
        )
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      const refundedArray = transactions.filter(
        (txn) => txn.status === 'refunded'
      )
      const uncollectedArray = []
      const collectedArray = []
      transactions.forEach((txn) => {
        if (txn.status !== 'refunded') {
          let uncollected = 0

          txn.items.forEach(({ qtyUncollected }) => {
            uncollected += qtyUncollected
          })

          if (uncollected === 0) collectedArray.push(txn)
          else uncollectedArray.push(txn)
        }
      })

      return [...uncollectedArray, ...refundedArray, ...collectedArray]
    },

    transaction () {
      const transaction = this.listofTransactions.find(txn => txn.uuid.includes(this.receiptId))
      const sortedItems = []
      this.skuSortOrder.forEach((sku) => {
        const item = transaction.items.find(item => item.sku === sku)
        if (item) sortedItems.push(item)
      })

      transaction.items = sortedItems
      return transaction
    },

    totalAmount () {
      // let total = 0
      // this.transaction.items.forEach(({ price }) => {
      //   total += price
      // })

      // return total
      return this.transaction.amount
    }
  },
  watch: {
    listofTransactions (newValue) {
      if (newValue.length > 0) setTimeout(this.paintCanvas, 50)
    }
  },

  // Lifecycle Hooks
  beforeCreate () {},
  created () {},
  beforeMount () {},
  mounted () {
    if (!this.isLoggedIn) {
      this.$router.push({ name: 'Login', query: this.$route.query })
    } else {
      if (this.transactionHistory.length === 0) {
        this.handleFetchTransactionHistory()
      } else {
        this.hasInit = true
      }
    }
  },
  beforeUpdate () {},
  updated () {},
  beforeDestroy () {},
  destroyed () {},
  methods: {
    ...mapActions(['updateTransactionHistory']),

    handleFetchTransactionHistory () {
      return new Promise((resolve, reject) => {
        this.getTransactionHistory().then(
          (response) => {
            this.updateTransactionHistory(response).then(() => {
              this.hasInit = true
              resolve()
            })
          },
          (error) => {
            console.error(error)
            this.createModal({
              title: 'Something went wrong',
              message: 'Failed to fetch transaction history.',
              btnMessage: 'Proceed',
              callback: () => {}
            })
            reject(error)
          }
        )
      })
    },

    printPage () {
      window.print()
    },

    paintCanvas () {
      var collection = document.querySelectorAll('.canvas')
      for (let i = 0; i < collection.length; i++) {
        const canvas = collection[i]
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = 'white'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }
    },

    isCollected (items) {
      // qtyUncollected > 0 = pending collection
      const uncollectedItem = items.find(item => item.qtyUncollected > 0)
      if (uncollectedItem === undefined) return true
      else return false
    },

    getAssets (sku) {
      return require(`../../assets/img/${sku}.png`)
    },

    routeToLocateVM (sku) {
      this.$router.push({ path: `/locate-vending-machines/${sku}` })
    }
  }
}
</script>

<style scoped>
@media only print {
  .print-container {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  .receipt-container {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  /* .content-container {
    width: 50%;
  } */
  .content-container {
    width: 100%;
  }

  /* .content-container:last-child:not(:nth-child(even)) {
    width: 50%;
    margin: 0 auto;
  } */
}
</style>
